import { Message, Comment as SemanticComment } from "semantic-ui-react";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { db, firebase } from "astrid-firebase";
import createComment from "astrid-firestore/src/api/discussion/createComment";
import { useCollectionData } from "astrid-firestore/src/hooks";

import LoadingContext from "../../../ui/components/LoadingContext/LoadingContext";
import useSortBy from "../../../ui/hooks/useSortBy";

import { useProfile } from "../../../authentication/state/profile";

import Comment from "../Comment/Comment";

import DiscussionForm from "./components/DiscussionForm";

export default function DiscussionThread({
	initialParticipants = [],
	notificationRef,
	onSetNeedsAttention,
	onLoaded,
	onCommentCreated,
	disabled,
	channels,
	disableScroll,
}) {
	const { t } = useTranslation();
	const user = useProfile();
	const bottomRef = useRef(null);

	const [comments, loading, error] = useCollectionData(
		db.collection("comments").where("channels", "array-contains-any", channels).where("deleted", "==", false),
	);

	const sortedComments = useSortBy(comments, "created.seconds", "asc");

	const filteredComments = sortedComments?.filter((comment) => !comment.replyTo);

	useEffect(() => {
		!disableScroll && bottomRef.current?.scrollIntoView();
	}, [disableScroll, loading]);

	const onComment = async ({ text, attachments, replyTo }) => {
		const comment = await createComment(firebase, {
			sender: user,
			createdBy: user,
			seenByUserIds: [user.id],
			text,
			attachments,
			channels,
			notificationRef,
			replyTo,
		});

		const participants = new Set(
			[...initialParticipants, ...comments.map(({ createdBy }) => createdBy?.id)].filter((id) => id !== user.id),
		);

		onCommentCreated?.(comment, participants);
	};

	return (
		<LoadingContext onLoaded={onLoaded} loading={loading} error={error}>
			<SemanticComment.Group style={{ marginBottom: 0 }}>
				{filteredComments?.map((comment) => (
					<Comment
						onSetNeedsAttention={onSetNeedsAttention}
						disabled={disabled}
						sortedComments={sortedComments}
						onComment={onComment}
						key={comment?.id}
						comment={comment}
					/>
				))}

				{!disabled && <DiscussionForm onComment={onComment} />}

				{disabled && filteredComments.length === 0 && (
					<Message>{t("noDiscussionAvailable", "No discussion available")}</Message>
				)}
			</SemanticComment.Group>

			<div ref={bottomRef}></div>
		</LoadingContext>
	);
}
